import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { CatalogCategoryFormState } from './catalogFormValues';
import { useCatalogCategorySave } from './useCatalogCategoryEditor';
import { useCatalogFormSubmit } from './useCatalogFormSubmit';
import { useCatalogLeaveConfirmation } from './useCatalogLeaveConfirmation';
import { CatalogCategoryImage } from './CatalogCategoryImage';
import { CategoryIdentitySection } from './CategoryIdentitySection';
import { CategoryPlacementSection } from './CategoryPlacementSection';
import { getAdminCatalogPath, getAdminCatalogCategoryDetailPath } from '../adminRoutes';
import { CatalogEntityHeader } from './CatalogEntityHeader';
import { CatalogEditorActions } from './CatalogEditorActions';
import { CatalogEditorNavigation } from './CatalogEditorNavigation';

interface CatalogCategoryFormProps {
  initialValues: CatalogCategoryFormState;
  entityId?: number;
}

export function CatalogCategoryForm({ initialValues, entityId }: CatalogCategoryFormProps) {
  const form = useForm<CatalogCategoryFormState>({ defaultValues: initialValues });
  const mutation = useCatalogCategorySave(entityId);
  const navigate = useNavigate();
  const isCreating = entityId === undefined;
  const isSaving = form.formState.isSubmitting || mutation.isPending;
  const rootError = form.formState.errors.root?.message;
  const confirmLeave = useCatalogLeaveConfirmation(form.formState.isDirty);
  const submit = useCatalogFormSubmit(form, mutation.mutateAsync, (savedEntity) => {
    const path = getAdminCatalogCategoryDetailPath(savedEntity.category_id);
    navigate(path, { state: { catalogSaved: true } });
  });
  const cancel = (): void => {
    if (!confirmLeave()) return;
    const path = entityId === undefined ? getAdminCatalogPath('categories') : getAdminCatalogCategoryDetailPath(entityId);
    navigate(path);
  };
  const title = isCreating ? 'Новая категория' : initialValues.category_name;
  const subtitle = isCreating ? 'Создание категории' : 'Редактирование категории';

  return (
    <FormProvider {...form}>
      <form className="catalog-editor-form" noValidate onSubmit={submit} aria-label="Форма категории">
        <CatalogEntityHeader kind="categories" title={title} subtitle={subtitle} isEditing actions={<CatalogEditorActions isSaving={isSaving} isDirty={form.formState.isDirty} isCreating={isCreating} onCancel={cancel} />} />
        {rootError ? <p className="catalog-feedback catalog-feedback_error" role="alert">{rootError}</p> : null}
        <div className="catalog-editor-body">
          <CatalogEditorNavigation isProduct={false} hasImages={false} />
          <div className="catalog-editor-sheet">
            <fieldset className="catalog-editor-fields" disabled={isSaving}>
              <CategoryIdentitySection />
              <CategoryPlacementSection categoryId={entityId} />
            </fieldset>
            {entityId !== undefined ? <CatalogCategoryImage categoryId={entityId} name={title} /> : <p>Сначала сохраните категорию, чтобы загрузить картинку для витрины.</p>}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
